import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Shield, ChevronLeft, ChevronRight } from "lucide-react";

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const pageSize = 30;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = { page, page_size: pageSize };
        if (typeFilter) params.entity_type = typeFilter;
        const { data } = await api.get("/audit-log", { params });
        setLogs(data.logs);
        setTotal(data.total);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [page, typeFilter]);

  const totalPages = Math.ceil(total / pageSize);

  const actionColor = (action) => {
    if (action === "CREATE") return "text-[#065F46] bg-[#065F46]/10";
    if (action === "DELETE" || action === "DISPOSE") return "text-[#991B1B] bg-[#991B1B]/10";
    if (action?.includes("APPROVE")) return "text-[#1E40AF] bg-[#1E40AF]/10";
    return "text-[#6B7280] bg-[#6B7280]/10";
  };

  return (
    <div className="space-y-4" data-testid="audit-log-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield size={24} className="text-[#C8A961]" />
          <div>
            <h1 className="font-display text-2xl font-semibold text-[#0B1020]">Audit Log</h1>
            <p className="text-sm text-[#6B7280] mt-1">Immutable record of all system mutations</p>
          </div>
        </div>
        <select className="stratum-input" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }} data-testid="audit-type-filter">
          <option value="">All Types</option>
          <option value="asset">Assets</option>
          <option value="journal">Journals</option>
          <option value="policy">Policies</option>
          <option value="capex">Capex</option>
          <option value="reassessment">Reassessments</option>
        </select>
      </div>

      <div className="stratum-card overflow-hidden">
        <table className="w-full stratum-table" data-testid="audit-log-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Action</th>
              <th>Entity Type</th>
              <th>Entity ID</th>
              <th>User</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="text-center py-8 text-[#6B7280]">Loading...</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-8 text-[#6B7280]">No audit entries</td></tr>
            ) : logs.map(log => (
              <tr key={log.id} data-testid={`audit-row-${log.id}`}>
                <td className="font-mono text-xs tabular-nums text-[#6B7280]">{log.timestamp?.substring(0, 19).replace("T", " ")}</td>
                <td><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${actionColor(log.action)}`}>{log.action}</span></td>
                <td className="text-sm capitalize">{log.entity_type}</td>
                <td className="font-mono text-xs text-[#6B7280] max-w-[120px] truncate">{log.entity_id?.substring(0, 12)}...</td>
                <td className="text-xs text-[#6B7280]">{log.user_id?.substring(0, 12)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#E8E2D5]">
            <span className="text-sm text-[#6B7280]">Page {page} of {totalPages} ({total} entries)</span>
            <div className="flex gap-2">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="p-2 rounded-lg border border-[#E8E2D5] disabled:opacity-30"><ChevronLeft size={16} /></button>
              <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="p-2 rounded-lg border border-[#E8E2D5] disabled:opacity-30"><ChevronRight size={16} /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
